import type { DataProvider } from '../types/types';

/** Minimum allowed page size for Datastore and REST data providers. */
export const PAGE_SIZE_MIN = 5;

/** Maximum allowed page size for Datastore and REST data providers. */
export const PAGE_SIZE_MAX = 5000;

/** Default fallback timeout (ms) for data preview API calls. */
export const DATA_PREVIEW_TIMEOUT_FALLBACK_MS = 10000;

/**
 * Page size for preview / data-provider POST payloads (HTTP pagination, Datastore, or SQL default).
 */
export function getDataProviderPreviewPageSize(
  dataProvider: DataProvider
): number {
  if (dataProvider.type === 'HTTP') {
    return dataProvider.httpApi.pagination?.pageSize ?? 10;
  }

  if (dataProvider.type === 'DATASTORE') {
    return dataProvider.pageSize ?? 10;
  }

  return 10;
}

/**
 * Validates and clamps a page size value within the allowed range.
 * Returns the clamped value, or the default if input is invalid.
 */
export function clampPageSize(value: number, defaultValue = 10): number {
  if (!Number.isFinite(value) || value < PAGE_SIZE_MIN) {
    return defaultValue;
  }

  if (value > PAGE_SIZE_MAX) {
    return PAGE_SIZE_MAX;
  }

  return value;
}

/** Unified error message returned when page size is out of range. */
export const PAGE_SIZE_ERROR = 'Page size must be between 5 and 5000';

/**
 * Returns a validation error message for page size, or empty string if valid.
 * Uses a single consistent message for all out-of-range cases so that
 * both the Data Store and API Configuration modals stay in sync.
 */
export function validatePageSize(value: string): typeof PAGE_SIZE_ERROR | '' {
  if (!value || value.trim() === '') {
    return '';
  }

  const n = parseInt(value, 10);

  if (Number.isNaN(n) || n < PAGE_SIZE_MIN || n > PAGE_SIZE_MAX) {
    return PAGE_SIZE_ERROR;
  }

  return '';
}
